/**
 * Signal chat picker (plan signal-connector.md §5.2). After the device is linked,
 * choose which groups and direct messages Brain should index.
 *
 * Two rules this UI has to be honest about:
 *   - DMs are forced private server-side, so there is deliberately NO per-DM
 *     visibility control — offering one would be a lie. The group toggle is the
 *     only visibility choice, and the DM section says so plainly.
 *   - Selecting submits the COMPLETE desired set, not an append. Chats already
 *     indexed are pre-checked, and un-checking one stops its ingest — so the
 *     footer calls out removals before the user commits.
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import { Check, Loader2, Lock, Search, User, Users } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { InfoNote } from '@/components/patterns/info-note';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { type SignalChat, signalAccountApi } from '@/lib/api';
import { qk } from '@/lib/query-client';
import { cn } from '@/lib/utils';

interface Props {
  accountId: string;
  onDone: () => void;
}

export function SignalChatPicker({ accountId, onDone }: Props) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [seeded, setSeeded] = useState(false);
  const [query, setQuery] = useState('');
  const [groupVisibility, setGroupVisibility] = useState<'workspace' | 'private'>('workspace');

  const { data: chats, isLoading } = useQuery({
    queryKey: [...qk.signalAccount(accountId), 'chats'],
    queryFn: () => signalAccountApi.chats(accountId),
  });

  // What's already indexed — pre-checked, because submitting replaces the set.
  const { data: already } = useQuery({
    queryKey: [...qk.signalAccount(accountId), 'selected'],
    queryFn: () => signalAccountApi.selectedChats(accountId),
  });

  const alreadyKeys = useMemo(() => new Set((already ?? []).map((ch) => ch.chatKey)), [already]);

  // Seed the checkboxes from the server's current selection exactly once, so a
  // returning user doesn't silently wipe their existing chats by saving.
  useEffect(() => {
    if (already && !seeded) {
      setSelected(new Set(already.map((ch) => ch.chatKey)));
      setSeeded(true);
    }
  }, [already, seeded]);

  const filtered = useMemo<SignalChat[]>(() => {
    const all = chats ?? [];
    const q = query.trim().toLowerCase();
    if (!q) return all;
    return all.filter((ch) => (ch.name ?? ch.chatKey).toLowerCase().includes(q));
  }, [chats, query]);

  const groups = useMemo(() => filtered.filter((ch) => ch.kind === 'group'), [filtered]);
  const dms = useMemo(() => filtered.filter((ch) => ch.kind === 'dm'), [filtered]);

  const selectMut = useMutation({
    mutationFn: () =>
      signalAccountApi.selectChats(accountId, {
        chatKeys: [...selected],
        visibility: groupVisibility,
      }),
    onSuccess: (res) => {
      if (res.skipped.length > 0) {
        toast.warning(
          `${res.skipped.length} chat${res.skipped.length === 1 ? '' : 's'} could no longer be found and ${res.skipped.length === 1 ? 'was' : 'were'} skipped.`,
        );
      }
      toast.success(`Indexing ${res.selected} chat${res.selected === 1 ? '' : 's'}.`);
      onDone();
    },
    onError: () => toast.error('Failed to save your selection. Please try again.'),
  });

  function toggle(chatKey: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(chatKey)) next.delete(chatKey);
      else next.add(chatKey);
      return next;
    });
  }

  const allFilteredSelected =
    filtered.length > 0 && filtered.every((ch) => selected.has(ch.chatKey));

  function toggleAll() {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const ch of filtered) {
        if (allFilteredSelected) next.delete(ch.chatKey);
        else next.add(ch.chatKey);
      }
      return next;
    });
  }

  // Previously-indexed chats the user has just un-checked — saving stops them.
  const removing = useMemo(
    () => [...alreadyKeys].filter((key) => !selected.has(key)),
    [alreadyKeys, selected],
  );

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-10 w-full rounded-lg" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    );
  }

  function renderRow(chat: SignalChat) {
    const isOn = selected.has(chat.chatKey);
    return (
      <button
        key={chat.chatKey}
        type="button"
        aria-pressed={isOn}
        onClick={() => toggle(chat.chatKey)}
        className="flex w-full items-center gap-3 border-border-subtle border-b px-4 py-3 text-left last:border-b-0 hover:bg-muted/40"
      >
        <span
          className={cn(
            'flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-colors',
            isOn ? 'border-primary bg-primary text-primary-foreground' : 'border-border',
          )}
        >
          {isOn && <Check className="h-3.5 w-3.5" />}
        </span>
        {chat.kind === 'group' ? (
          <Users className="h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <User className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}
        <span className="min-w-0 flex-1 truncate text-[14px]">{chat.name || chat.chatKey}</span>
        {chat.kind === 'group' && chat.memberCount != null && (
          <span className="shrink-0 text-[12px] text-faint">{chat.memberCount} members</span>
        )}
        {chat.kind === 'dm' && (
          <span className="inline-flex shrink-0 items-center gap-1 text-[11.5px] text-faint">
            <Lock className="h-3 w-3" /> Private
          </span>
        )}
      </button>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold">Choose chats to index</h2>
        <p className="mt-1.5 text-[14px] text-muted-foreground">
          Pick the groups and direct messages Brain should make searchable. You can change this any
          time.
        </p>
      </div>

      {/* Group visibility — the ONLY visibility control, because DMs are forced
          private server-side (a per-DM toggle would be a lie). */}
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border-subtle p-3 text-[13px]">
        <span className="text-muted-foreground">Selected groups are</span>
        <div className="inline-flex overflow-hidden rounded-md border border-border">
          {(['workspace', 'private'] as const).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setGroupVisibility(v)}
              className={cn(
                'px-3 py-1 text-[12.5px] transition-colors',
                groupVisibility === v
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {v === 'workspace' ? 'Shared with workspace' : 'Private to me'}
            </button>
          ))}
        </div>
        <span className="ml-auto inline-flex items-center gap-1 text-faint">
          <Lock className="h-3 w-3" /> DMs are always private to you
        </span>
      </div>

      {/* Search + select all */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="-translate-y-1/2 absolute top-1/2 left-3 h-4 w-4 text-faint" />
          <Input
            placeholder="Search chats…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button variant="outline" size="sm" onClick={toggleAll} disabled={filtered.length === 0}>
          {allFilteredSelected ? 'Clear' : 'Select all'}
        </Button>
      </div>

      {/* Chat list, split so the DM privacy rule sits next to the DMs. */}
      {filtered.length === 0 ? (
        <div className="rounded-xl border border-border p-6 text-center text-[13px] text-faint">
          {chats && chats.length === 0
            ? 'No groups or contacts yet. They appear here once Signal syncs them to this device.'
            : 'No chats match your search.'}
        </div>
      ) : (
        <div className="space-y-4">
          {groups.length > 0 && (
            <section>
              <h3 className="mb-2 font-mono text-[11px] uppercase tracking-[0.06em] text-faint">
                Groups
              </h3>
              <div className="max-h-[240px] overflow-y-auto rounded-xl border border-border">
                {groups.map(renderRow)}
              </div>
            </section>
          )}
          {dms.length > 0 && (
            <section>
              <h3 className="mb-2 font-mono text-[11px] uppercase tracking-[0.06em] text-faint">
                Direct messages
              </h3>
              <div className="max-h-[240px] overflow-y-auto rounded-xl border border-border">
                {dms.map(renderRow)}
              </div>
            </section>
          )}
        </div>
      )}

      {/* Saving replaces the selection — say so when it would remove something. */}
      {removing.length > 0 && (
        <InfoNote icon={Lock}>
          {removing.length} chat{removing.length === 1 ? '' : 's'} you previously indexed{' '}
          {removing.length === 1 ? 'is' : 'are'} now unchecked. Saving stops new messages from{' '}
          {removing.length === 1 ? 'it' : 'them'} — already-indexed messages stay searchable.
        </InfoNote>
      )}

      <div className="flex items-center gap-3">
        <span className="text-[13px] text-faint">{selected.size} selected</span>
        <Button
          size="cta"
          className="ml-auto"
          disabled={selectMut.isPending || (selected.size === 0 && removing.length === 0)}
          onClick={() => selectMut.mutate()}
        >
          {selectMut.isPending ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Saving…
            </>
          ) : selected.size === 0 ? (
            'Stop indexing all chats'
          ) : (
            `Index ${selected.size} chat${selected.size === 1 ? '' : 's'}`
          )}
        </Button>
      </div>
    </div>
  );
}
